/**
 * `workOrdersApi` — standalone RTK Query slice for the proxy-server
 * federated work-orders surface (`/api/v1/work-orders*`).
 *
 * Precedent: `notifications-api.ts` — proxy-direct (no BFF hop), own
 * cache + tag space. Composite ids contain `/` and must be URL-encoded
 * in path segments.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { PROXY_API_V1_URL, ontologyBaseQuery } from '../core'
import type {
  CommentWorkOrderPayload,
  CompleteWorkOrderPayload,
  WorkOrder,
  WorkOrderListFilters,
  WorkOrderListResult
} from './types'

export const WORK_ORDERS_TAG_TYPES = ['WorkOrder'] as const
export type WorkOrdersTagType = (typeof WORK_ORDERS_TAG_TYPES)[number]

function encodeWorkOrderId(id: string): string {
  return encodeURIComponent(id)
}

function listParams(
  filters: WorkOrderListFilters | void
): Record<string, string | number | undefined> {
  if (!filters) return {}
  const priority =
    Array.isArray(filters.priority) && filters.priority.length > 0
      ? filters.priority.join(',')
      : typeof filters.priority === 'string' && filters.priority.trim().length > 0
        ? filters.priority.trim()
        : undefined
  return {
    status: filters.status,
    priority,
    q: filters.q,
    source: filters.source,
    page_size: filters.page_size,
    page_token: filters.page_token
  }
}

export const workOrdersApi = createApi({
  reducerPath: 'workOrdersApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: WORK_ORDERS_TAG_TYPES,
  endpoints: (builder) => ({
    listWorkOrders: builder.query<WorkOrderListResult, WorkOrderListFilters | void>({
      query: (filters) => ({
        url: `${PROXY_API_V1_URL}/work-orders`,
        params: listParams(filters)
      }),
      transformResponse: (response: WorkOrderListResult | null): WorkOrderListResult => ({
        work_orders: Array.isArray(response?.work_orders) ? response.work_orders : [],
        next_page_token: response?.next_page_token,
        source_errors: Array.isArray(response?.source_errors) ? response.source_errors : undefined
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.work_orders.map((wo) => ({ type: 'WorkOrder' as const, id: wo.id })),
              { type: 'WorkOrder' as const, id: 'LIST' }
            ]
          : [{ type: 'WorkOrder' as const, id: 'LIST' }]
    }),
    getWorkOrder: builder.query<WorkOrder, string>({
      query: (id) => `${PROXY_API_V1_URL}/work-orders/${encodeWorkOrderId(id)}`,
      providesTags: (_r, _e, id) => [{ type: 'WorkOrder', id }]
    }),
    completeWorkOrder: builder.mutation<WorkOrder, { id: string; body: CompleteWorkOrderPayload }>({
      query: ({ id, body }) => ({
        url: `${PROXY_API_V1_URL}/work-orders/${encodeWorkOrderId(id)}:complete`,
        method: 'POST',
        body
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'WorkOrder', id },
        { type: 'WorkOrder', id: 'LIST' }
      ]
    }),
    addWorkOrderComment: builder.mutation<WorkOrder, { id: string; body: CommentWorkOrderPayload }>(
      {
        query: ({ id, body }) => ({
          url: `${PROXY_API_V1_URL}/work-orders/${encodeWorkOrderId(id)}:comment`,
          method: 'POST',
          body
        }),
        // Comment response carries updated comments — patch the detail cache
        // and refresh the list (comment counts / ordering may change).
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled
            dispatch(workOrdersApi.util.updateQueryData('getWorkOrder', id, () => data))
          } catch {
            // Leave cache alone; invalidation below still refreshes.
          }
        },
        invalidatesTags: (_r, _e, { id }) => [
          { type: 'WorkOrder', id },
          { type: 'WorkOrder', id: 'LIST' }
        ]
      }
    )
  })
})

export const {
  useListWorkOrdersQuery,
  useGetWorkOrderQuery,
  useCompleteWorkOrderMutation,
  useAddWorkOrderCommentMutation
} = workOrdersApi
